import gql from "graphql-tag";
import {
    getInventoryForMaterial,
    listInventory
} from "../../graphql/inventory-overview-api/queries";
import {
    entryContextFallbackSearch,
    getInventoryQuantityBySkuContainerBol,
    getInventoryReports,
    inventoryDetailSearch,
    inventoryDynamicSearch,
    listTransactionsFieldSearch,
    getDashboard,
    transactionsDynamicSearch
} from "../../graphql/audit-overview-api/queries";
import {
    submitCreateInventoryReport,
    submitManualAdjustment,
    submitManualDrsOrTransitAdjustment
} from "../../graphql/audit-overview-api/mutations";
import {CustomsStatus, Type} from "../../graphql/audit-overview-api/api";
import {AppsyncService} from "./AppsyncService";
import {oktaAuth} from "../config/OktaConfig";
import {getPayloadUrlOnly, listFlowFieldSearch} from "../../graphql/flow-overview/queries";


export class InventoryService {

    inventoryOverviewAppsyncService : AppsyncService
    auditOverviewAppsyncService : AppsyncService
    flowOverviewAppsyncService : AppsyncService

    constructor() {
        this.inventoryOverviewAppsyncService = new AppsyncService(process.env.REACT_APP_APPSYNC_INVENTORY_OVERVIEW_ENDPOINT!, oktaAuth)
        this.auditOverviewAppsyncService = new AppsyncService(process.env.REACT_APP_APPSYNC_AUDIT_OVERVIEW_ENDPOINT!, oktaAuth)
        this.flowOverviewAppsyncService = new AppsyncService(process.env.REACT_APP_APPSYNC_FLOW_OVERVIEW_ENDPOINT!, oktaAuth)
    }

    async getInventoryForMaterial(material: string, nextToken: string): Promise<any> {
        return this.inventoryOverviewAppsyncService.appSyncClient.query({
            query: gql(getInventoryForMaterial), fetchPolicy: 'network-only', variables: {
                material: material,
                nextToken: nextToken
            }
        })
    }

    async getDashboard(): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(getDashboard), fetchPolicy: 'network-only', variables: {
            }
        })
    }

    async getInventoryForMaterialSizeCoo(material: string, size: string, countryOfOrigin: string, quality: string): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(inventoryDetailSearch), fetchPolicy: 'network-only', variables: {
                material: material,
                size: size,
                countryOfOrigin: countryOfOrigin,
                quality: quality
            }
        })
    }

    async listInventory(nextToken: string): Promise<any> {
        return this.inventoryOverviewAppsyncService.appSyncClient.query({
            query: gql(listInventory), fetchPolicy: 'network-only', variables: {
                nextToken: nextToken
            }
        })
    }

    async listTransactions(material: string, size: string, countryOfOrigin: string, quality: string, startDate: Date, endDate: Date,
                           location: string, customsStatus: string, type: string, reason: string, source: string): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(transactionsDynamicSearch), fetchPolicy: 'network-only', variables: {
                material: material,
                size: size,
                countryOfOrigin: countryOfOrigin,
                quality: quality,
                fromTimestamp: startDate != null ? startDate.getTime() : null,
                toTimestamp: endDate != null ? endDate.getTime() : null,
                location: location != null ? location : null,
                customsStatus: customsStatus != null ? customsStatus : null,
                type: type != null ? type : null,
                reason: reason,
                source: source
            }
        })
    }

    async listFallbackEntryContexts(): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(entryContextFallbackSearch), fetchPolicy: 'network-only', variables:
                {}
        })
    }

    async getAuditInventoryForMaterial(material: string,
                                       size: string,
                                       countryOfOrigin: string,
                                       quality: string,
                                       imbalanceFilter: string,
                                       nextToken: string): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(inventoryDynamicSearch), fetchPolicy: 'network-only', variables: {
                material: material,
                size: size,
                countryOfOrigin: countryOfOrigin,
                quality: quality,
                imbalanceFilter: imbalanceFilter,
                nextToken: nextToken
            }
        })
    }

    async listAuditFieldOptions(fieldName: string): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(listTransactionsFieldSearch), fetchPolicy: 'network-only', variables: {
                fieldName: fieldName
            }
        });
    }

    async listFlowFieldOptions(fieldName: string): Promise<any> {
        return this.flowOverviewAppsyncService.appSyncClient.query({
            query: gql(listFlowFieldSearch), fetchPolicy: 'network-only', variables: {
                fieldName: fieldName
            }
        });
    }

    async getInventoryReports(): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(getInventoryReports), fetchPolicy: 'network-only', variables:
                {}
        })
    }

    async submitManualAdjustment(
        material: string,
        size: string,
        countryOfOrigin: string,
        quality: string,
        location: string,
        type: Type,
        quantity: number,
        customsStatus: CustomsStatus,
        reason: string,
        comment: string,
        clearGoodsIfNeeded: boolean
    ): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.mutate({
            mutation: gql(submitManualAdjustment), variables: {
                material: material,
                size: size,
                countryOfOrigin: countryOfOrigin,
                quality: quality,
                location: location,
                type: type,
                quantity: quantity,
                customsStatus: customsStatus,
                reason: reason,
                comment: comment,
                clearGoodsIfNeeded: clearGoodsIfNeeded
            }
        });
    }

    async submitManualDrsOrTransitAdjustment(
        material: string,
        size: string,
        countryOfOrigin: string,
        quality: string,
        location: string,
        type: Type,
        quantity: number,
        reason: string,
        comment: string,
        container: string,
        bol: string,
        clearGoodsIfNeeded: boolean
    ): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.mutate({
            mutation: gql(submitManualDrsOrTransitAdjustment),
            variables: {
                material: material,
                size: size,
                countryOfOrigin: countryOfOrigin,
                quality: quality,
                location: location,
                type: type,
                quantity: quantity,
                reason: reason,
                comment: comment,
                container: container,
                bol: bol,
                clearGoodsIfNeeded: clearGoodsIfNeeded
            }
        });
    }

    async getInventoryQuantityBySkuContainerBol(
        material: string,
        size: string,
        countryOfOrigin: string,
        quality: string,
        container: string,
        bol: string
    ): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.query({
            query: gql(getInventoryQuantityBySkuContainerBol),
            fetchPolicy: 'network-only',
            variables: {
                material: material,
                size: size,
                countryOfOrigin: countryOfOrigin,
                quality: quality,
                container: container,
                bol: bol
            }
        });
    }

    async getPayloadUrlOnly(region: string, bucket: string, key: string, version: string): Promise<any> {
        return this.flowOverviewAppsyncService.appSyncClient.query({
                query: gql(getPayloadUrlOnly),
                fetchPolicy: 'network-only',
                variables: {
                    region: region,
                    bucket: bucket,
                    key: key,
                    version: version
                }
            }
        );
    }

    async submitCreateInventoryReport(): Promise<any> {
        return this.auditOverviewAppsyncService.appSyncClient.mutate({
            mutation: gql(submitCreateInventoryReport), variables: {
                reportType: 'STOCK'
            }
        });
    }


}